import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import moment from "moment";
import _debounce from "lodash/debounce";

import useOutsideClick from "hooks/useOutsideClick";
import { usePaymentPlanDataState } from "hooks/usePaymentPlanState";
import {
  cardToShortname,
  downloadPdfFromApi,
  encode,
  getGeneralDetailMarkers,
  getMethod,
  percentageCount,
  shortToCardName,
  validateEmail,
} from "utils/HelperFunctions";
import {
  averageAmount,
  calculateAmountOutstanding,
  CalculateCustomPercentage,
} from "utils/amount/amountCalculate";
import {
  getPaymentPlanDates,
  leadData,
  setIsDashPreview,
  useLeadData,
  usePaymentPlanData as usePaymentPlanDataRedux,
  usePortalSettingsData,
  usePortalVanityUrl,
} from "store/slices/dashboardSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import {
  addCard,
  useBankData,
  useCardData,
  useIsDataLoading,
  useOldWalletData,
} from "store/slices/paymentMethodSlice";
import {
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  createPaypalPaymentToken,
  createPaypalToken,
  duplicatePaymentGet,
  paymentDownload,
  paymentReceiptDownload,
  storePayPalOrder,
  updatePayment,
  wirePaymentProcess,
  paymentReceiptDownloadPdf,
  sendPaymentPlanUpdatedEmail,
} from "store/slices/paymentsSlice";
import {
  useIsLogin,
  useShowFinalDisclosure,
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
} from "store/slices/commonSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { decode } from "utils/HelperFunctions";
import { valuesProps } from "types/paymentMethods";
import { initialValues } from "utils/countryAddressList";
import { SettingsValue } from "types/payments";
import { useMediaQuery } from "react-responsive";
import { RootState } from "store/store";
import { UpdatePlanProps, Transaction } from "types/updatePaymentPlan";
import {
  calculateScheduleSummary,
  createPaymentMethodSelection,
  createPaymentPlanScheduleSnapshot,
  createPaymentPlanUpdateFields,
  getFixedScheduleInstallmentAmount,
  getAuthoritativePaymentPlanState,
  getMissingScheduleDateError,
  MISSING_SCHEDULE_DATE_MESSAGE,
  getPaymentPlanStartDate,
  getPaymentPlanUpdateFlags,
  getScheduleEndDateError,
  getScheduleTransactionCount,
  getTransactionConvenienceFee,
  getTransactionFeeForPaymentMethod,
  getOperatingSystem,
  getTransactionScheduleDate,
  getUpdateBillingAddress,
  getUpdatePlanFailure,
  isLatestScheduleRequest,
  normalizeUpdatePlanTransactions,
  normalizeScheduleFeesForPaymentMethod,
  reconcileUpdatePlanScheduleTransactions,
  redistributePendingScheduleAmounts,
  runPaymentPlanUpdateOnce,
  shouldDisablePaymentPlanUpdate,
  sortDates,
  updateTransactionScheduleDate,
  PaymentMethodSelection,
  PaymentPlanScheduleSnapshot,
} from "utils/updatePaymentPlanUtils";
import { sendScheduleUpdatedEmailAfterUpdate } from "utils/paymentPlanUpdateNotification";
import {
  UpdatePlanHeader,
  UpdatePlanContent,
  SuccessWirePayment,
  SuccessRetryPayment,
  SuccessDefaultPayment,
  FailedPayment,
  UpdateButtons,
} from "./UpdatePaymentPlan/index";
import { dateToString } from "utils/DateFormat";
import { useTid } from "utils/testid";
import MessageToast from "components/ui-component/MessageToast";

interface UpdatePlanPropsLocal extends UpdatePlanProps {}

// Type definitions for local use
type HandleChangeType = "date" | "amount" | "percent";
type HandleTabChangeType = "one_time" | "schedule";

type PaymentPlan = {
  transactions: Transaction[];
  grand_total?: number;
  amount_subtotal?: number;
  percentage_amounts?: number[];
};

const failedScheduleStatuses = ["failed", "declined"];

const isFailedScheduleTransaction = (transaction: any) =>
  failedScheduleStatuses.includes(
    `${transaction?.status || ""}`.trim().toLowerCase()
  );

const getTransactionDateValue = (transaction: any) =>
  transaction?.billing_date || transaction?.payment_date || transaction?.date;

const getPrimaryEmail = (data: any, userData: any, fallback = "") =>
  data?.clientCustomerData?.email ||
  data?.clientCustomerData?.billing_email ||
  data?.customerData?.email ||
  userData?.data?.accountData?.email ||
  fallback ||
  "";

const getPaymentMethodCode = (method: string) => {
  switch (method) {
    case "Bank":
      return "ACH";
    case "PayPal":
      return "PP";
    case "Cash App Pay":
      return "CAPP";
    case "Amazon Pay":
      return "AMZP";
    case "Google Pay":
      return "GP";
    case "Apple Pay":
      return "AP";
    case "Wire":
      return "WTO";
    case "Card":
      return "CC";
    case "CC":
    case "ACH":
    case "PP":
    case "CAPP":
    case "AMZP":
    case "GP":
    case "AP":
    case "WTO":
      return method;
    default:
      return "CC";
  }
};

const normalizeScheduleValue = (value: any) =>
  `${value ?? ""}`.trim().toLowerCase();

const getScheduleAmountType = (paymentPlan: any) =>
  normalizeScheduleValue(
    paymentPlan?.schedule_amount ||
      paymentPlan?.transactions?.[0]?.schedule_type
  );

const getTransactionInputType = (paymentPlan: any) => {
  const transactionType = normalizeScheduleValue(paymentPlan?.transaction_type);

  if (transactionType === "percent" || transactionType === "percentage") {
    return "percent";
  }

  if (transactionType === "amount") {
    return "amount";
  }

  const amountType = `${paymentPlan?.transactions?.[0]?.amount_type || ""}`
    .trim()
    .toUpperCase();

  return amountType === "P" ? "percent" : "amount";
};

const getAmountTypeCode = (paymentPlan: any) =>
  getTransactionInputType(paymentPlan) === "percent" ? "P" : "A";

type PaymentPlanUpdateFlags = {
  updateMethod: boolean;
  updateSchedule: boolean;
};

const parseUpdateFlag = (value: unknown): boolean =>
  value === true || `${value}`.trim().toLowerCase() === "true";

const UpdatePaymentPlan = (props: UpdatePlanPropsLocal) => {
  const tid = useTid();
  const {
    id,
    show,
    setShow,
    setUpdatePlanId,
    setIsPreview,
    setHandleDrawerSize,
    setIsPreviewId = () => {},
    setUpdateData,
    isApiCall,
    setIsApiCall,
    setFinalPreview,
    setFinalPreviewId,
    isFinalPreview,
    fetchDetails = () => {},
    setSelectedMethod: setMethodPreview = () => {},
  } = props;
  const dispatch = useAppDispatch();
  const accountLeadData = useLeadData();
  const userData = useUserData();
  const accountData = userData?.data?.accountData;
  const {
    payPalOrder,
    paymentPermission,
    paymentPlan: verifyPaymentPlan,
    checkedDuplicatePayment,
  } = useAppSelector((state: RootState) => state.payment);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isLogin = useIsLogin();
  const { clusterId: cluster_id, id: slug } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const navigate = useNavigate();
  const portalSettings = usePortalSettingsData();
  const vanityUrl = usePortalVanityUrl();
  const showFinalDisclosure = useShowFinalDisclosure();
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });

  useEffect(() => {
    // Always hand the flag back when this preview goes away or the screen
    // grows past the mobile breakpoint. Without that, navigating away with
    // the preview open left it stuck on, and the layout kept the mobile
    // header and footer hidden on every page.
    if (!isLessThan590) {
      dispatch(setIsDashPreview?.(false));
      return;
    }
    dispatch(setIsDashPreview?.(show));
    return () => {
      dispatch(setIsDashPreview?.(false));
    };
  }, [show, isLessThan590]);

  // Handle lead data loading
  useEffect(() => {
    if (
      isLogin &&
      accountLeadData &&
      Object.keys(accountLeadData).length === 0 &&
      show
    ) {
      const leadPayload = {
        accounts_id: userData.data.accountData.id,
        cluster_id: userData.data.accountData.clusterid,
        accountid: userData.data.accountData.accountid,
      };
      dispatch(leadData(leadPayload)).catch((error: any) => {
        console.log("onGetLeadData error", error);
      });
    }
  }, [isLogin, show, accountLeadData, userData, dispatch]);

  const isDataLoading = useIsDataLoading();

  // Redux payment plan data
  const paymentPlanDataRedux = usePaymentPlanDataRedux();

  // Initialize state for payment data
  const [data, setData] = useState<any>({
    lead_id: "",
    update_lead: "Y",
    clusterid: "434",
    p_accound_id: "454",
    first_name: "",
    last_name: "",
    lead_owner: "",
    paymentPlan: {
      amount: 0,
      transactions: [],
      amount_subtotal: 0,
      grand_total: 0,
      payment_schedule: "",
      no_of_transaction: 0,
      start_date: "",
      schedule_amount: "",
      schedule_fld: "",
      transaction_type: "",
    },
  });

  // Initialize state for form/payment data
  const initialState = {
    filterParamValue: "",
    filterParam: "search",
    page: 1,
    state: "AL",
    country: "US",
    isrecurring: "oneTime",
    agreementType: "new",
    type: "Amount",
    amount: "0.00",
    new_offer_type: "Amount",
    paymentPlan: { start_date: moment().format("MM/DD/YYYY") },
    selectedCard: {},
    selectedBank: {},
  };

  const [dataPayment, setDataPayment] = useState<any>(initialState);
  const [initialDisclosureAccept, setInitialDisclosureAccept] =
    useState<boolean>(
      portalSettings?.state_disclosure_flag === SettingsValue.OFF
    );

  const calenderRef = useRef<any>();

  const [values, setValues] = useState<valuesProps>(initialValues);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const [isUpdateRetryLoading, setIsUpdateRetryLoading] =
    useState<boolean>(false);
  const [completedSteps, setCompletedSteps] = useState<any>([0]);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isWirePayment, setIsWirePayment] = useState<boolean>(false);
  const [invoiceId, setInvoiceId] = useState<any>("");
  const [wireDetails, setWireDetails] = useState({
    email: "",
    bank: "",
    routing: "",
    account: "",
    swift: "",
    dueDate: "",
  });
  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const [selectedMethod, setSelectedMethod] = useState<
    "Card" | "Bank" | "PayPal" | "Cash App Pay" | string
  >("Card");
  const [isAddNewCard, setIsAddNewCard] = useState<boolean>(false);
  const [payment_scheduleId, setPaymentScheduleId] = useState("");
  const [planSuccess, setPlanSuccess] = useState<boolean>(true);
  const [failedReason, setFailedReason] = useState<string>(
    "We couldn't update your payment plan. Please try again."
  );
  // Distinguishes a gateway decline from a plan-update error, so the failure
  // screen only blames the customer's payment method when that is the cause.
  const [isDeclinedFailure, setIsDeclinedFailure] = useState<boolean>(false);
  const [savedCardsEmail, setSavedCardsEmail] = useState<string>("");
  const [isSelectStartDate, setIsSelectStartDate] = useState<boolean>(false);
  const [isRetryPayment, setIsRetryPayment] = useState<boolean>(false);
  const [failedPayment, setFailedPayment] = useState<any>(0);
  const [emailDisplay, setEmailDisplay] = useState<string>("");
  const [emailInput, setEmailInput] = useState("");
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);
  const [isStartDate, setIsStartDate] = useState<boolean>(false);
  const [isEndDateError, setIsEndDateError] = useState<boolean>(false);
  const [dateErrMsg, setDateErrMsg] = useState<string | null>(null);
  const [emailDeliveryWarning, setEmailDeliveryWarning] = useState<
    string | null
  >(null);
  const [showButtons, setShowButtons] = useState<boolean>(false);
  const [isUpdatePaymentPlan, setIsUpdatePaymentPlan] =
    useState<boolean>(false);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [disabledCount, setDisabledCount] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [updatedArr, setUpdatedArr] = useState<any>({
    updatedValue: [],
    createdValue: [],
    deletedValue: [],
  });
  const shouldShowRetryPayment =
    isRetryPayment && Number(failedPayment || 0) > 0;
  const hasRefreshedAfterSuccess = useRef(false);
  const hasUserEditedSchedule = useRef(false);
  const hydratedPlanId = useRef<string>("");
  const initialScheduleSnapshotRef = useRef<PaymentPlanScheduleSnapshot | null>(
    null
  );
  const initialPaymentMethodRef = useRef<PaymentMethodSelection | null>(null);
  const updateRequestInFlightRef = useRef(false);
  const updateFlowInFlightRef = useRef(false);
  const emailNotificationOutcomesRef = useRef<Map<string, string | null>>(
    new Map()
  );
  useEffect(() => {
    if (!show) {
      setEmailDeliveryWarning(null);
      emailNotificationOutcomesRef.current.clear();
    }
  }, [show]);
  const [userPaymentMethodSelection, setUserPaymentMethodSelection] =
    useState<PaymentMethodSelection | null>(null);
  const paymentPlanBeforeAddRef = useRef<any>(null);
  const latestScheduleRequestRef = useRef(0);
  const { updateMethod, updateSchedule } = getPaymentPlanUpdateFlags(
    initialScheduleSnapshotRef.current,
    data?.paymentPlan,
    initialPaymentMethodRef.current,
    userPaymentMethodSelection
  );
  const hasPlanChanges = updateMethod || updateSchedule;
  const isAnyUpdateLoading =
    isUpdateLoading || isUpdateRetryLoading || isGoogleLoading;
  const currentUpdateFlags = useMemo<PaymentPlanUpdateFlags>(
    () => ({ updateMethod, updateSchedule }),
    [updateMethod, updateSchedule]
  );
  const isUpdateDisabled = shouldDisablePaymentPlanUpdate({
    flags: currentUpdateFlags,
    isRetry: shouldShowRetryPayment,
    isSubmitting: isAnyUpdateLoading,
    isScheduleBusy: isLoading || Boolean(isApiCall),
    hasScheduleError: isEndDateError,
    hasPaymentMethodError: isDisabled,
  });
  const handlePaymentMethodChange = useCallback(
    (selection: PaymentMethodSelection) => {
      setUserPaymentMethodSelection(
        createPaymentMethodSelection(
          selection.method,
          selection.paymentMethodId,
          selection.isNew
        )
      );
    },
    []
  );

  const showPlanUpdateFailure = (responseBody?: any, error?: unknown) => {
    const failure = getUpdatePlanFailure(responseBody, error) || {
      reason: "We couldn't update your payment plan. Please try again.",
      isDeclined: false,
    };

    setFailedReason(failure.reason);
    setIsDeclinedFailure(failure.isDeclined);
    setIsUpdatePaymentPlan(true);
    setPlanSuccess(false);
  };

  useEffect(() => {
    if (
      isUpdatePaymentPlan &&
      planSuccess &&
      !hasRefreshedAfterSuccess.current
    ) {
      hasRefreshedAfterSuccess.current = true;
      fetchDetails();
    }

    if (!isUpdatePaymentPlan) {
      hasRefreshedAfterSuccess.current = false;
    }
  }, [isUpdatePaymentPlan, planSuccess, fetchDetails]);

  console.log("selectedMethod:-->", selectedMethod);

  const handlePlanStateUpdate = (newData: any) => {
    if (newData?.customerData && newData?.clientCustomerData) {
      let initial_balance = Number(
        newData?.clientCustomerData?.initial_balance || 0
      );
      let balance = Number(newData?.clientCustomerData?.initial_balance || 0);
      let disable_arr = [];
      let last_bal = Number(newData?.clientCustomerData?.initial_balance || 0);
      let pendingBalance = 0;
      let last_date = 0;
      let sum = 0;
      let disabled_date = 0;
      let calculated = [];
      let percentage_amounts = [];
      let total_fee = 0;
      let old_method = newData?.clientCustomerData?.payment_method || "CC";

      setSelectedMethod(shortToCardName(old_method) || "Card");

      let amount_type =
        newData?.clientCustomerData?.clientScheduleCustomData[0]
          ?.amount_type === "P"
          ? "percent"
          : "amount";

      if (amount_type === "amount") {
        calculated = newData?.clientCustomerData?.clientScheduleCustomData?.map(
          (history: any) => {
            balance = balance - Number(history?.amount);
            const payment_date = dateToString(history?.billing_date);
            if (history?.status !== "Pending") disabled_date += 1;

            total_fee += Number(history?.cf_amount);

            let obj = {
              payment_date,
              amount: Number(history?.amount).toFixed(2),
              fee: history?.cf_amount,
              schedule_type: history?.schedule_type,
              amount_type: history?.amount_type,
              balance,
              id: history?.id,
              status: history?.status,
            };

            if (history?.status !== "Pending") {
              disable_arr.push(obj);
              last_bal = balance;
              sum += history?.amount;
              pendingBalance = balance;
            } else if (history?.status === "Pending" && last_date === 0) {
              last_date = history?.billing_date;
            }

            return obj;
          }
        );
      } else {
        calculated = newData?.clientCustomerData?.clientScheduleCustomData?.map(
          (history: any) => {
            const amount = (history?.percentage / 100) * initial_balance;
            balance = balance - amount;
            percentage_amounts.push(amount);

            const payment_date = dateToString(history?.billing_date);
            if (history?.status !== "Pending") disabled_date += 1;

            total_fee += Number(history?.cf_amount);

            let obj = {
              payment_date,
              amount: history?.percentage,
              fee: history?.cf_amount,
              schedule_type: history?.schedule_type,
              amount_type: history?.amount_type,
              balance,
              id: history?.id,
              status: history?.status,
            };

            if (history?.status !== "Pending") {
              disable_arr.push(obj);
              last_bal = balance;
              sum += history?.amount;
            } else if (history?.status === "Pending" && last_date === 0) {
              last_date = history?.billing_date;
            }

            return obj;
          }
        );
      }

      if (last_date === 0) {
        const currStart = dateToString(
          newData?.clientCustomerData?.clientScheduleCustomData[0]?.billing_date
        );
        setStartDate(currStart);
      } else {
        const currStart = dateToString(last_date);
        setStartDate(currStart);
      }

      setPendingBalance(pendingBalance);
      setDisabledCount(disabled_date);

      const failedTransactions =
        newData?.clientCustomerData?.clientScheduleCustomData
          ?.filter(isFailedScheduleTransaction)
          ?.sort(
            (a: any, b: any) =>
              moment(getTransactionDateValue(b)).valueOf() -
              moment(getTransactionDateValue(a)).valueOf()
          ) || [];
      const lastFailedTransaction = failedTransactions[0];
      const failedAmount = Number(lastFailedTransaction?.amount || 0);

      setIsRetryPayment(failedAmount > 0);
      setFailedPayment(failedAmount > 0 ? failedAmount : 0);
      setEmailDisplay(getPrimaryEmail(newData, userData));
    }
  };

  // Initialize custom hooks
  const paymentPlanStateHooks = usePaymentPlanDataState(
    (newData: any) => {
      setData((prev: any) => ({ ...prev, ...newData }));
      handlePlanStateUpdate(newData);
    },
    setIsApiCall || (() => {})
  );

  const onClose = () => {
    setShow(false);
    setUpdatedArr({
      updatedValue: [],
      createdValue: [],
      deletedValue: [],
    });

    if (isUpdatePaymentPlan) {
      fetchDetails();
    }
    setIsUpdatePaymentPlan(false);
    setCurrentStep(1);
    setCompletedSteps([0]);
    setDataPayment(initialState);
    setIsRetryPayment(false);
    setFailedPayment(0);
    setEmailDisplay("");
    setEmailDeliveryWarning(null);
    emailNotificationOutcomesRef.current.clear();
    hasUserEditedSchedule.current = false;
    hydratedPlanId.current = "";
    initialScheduleSnapshotRef.current = null;
    initialPaymentMethodRef.current = null;
    setUserPaymentMethodSelection(null);
    latestScheduleRequestRef.current += 1;
    if (setIsApiCall) setIsApiCall(false);
    setUpdatePlanId();
    setValues(initialValues);
    setInitialDisclosureAccept(false);
    if (!isLogin) {
      let clusterId = cluster_id
        ? cluster_id
        : userData?.data?.accountData?.clusterid
          ? encode(`${userData?.data?.accountData?.clusterid}`, "ci_")
          : encode(decode(slug || "")?.split("_")[1], "ci_");

      navigate(`/${clusterId}/dashboard`);
    }
  };

  const handleBackToStart = () => {
    setCurrentStep(2);
    setCompletedSteps([0, 1]);
    setIsUpdatePaymentPlan(false);
    setPlanSuccess(true);
    setValues(initialValues);
  };

  useOutsideClick(calenderRef, () => {
    setIsStartDate(false);
  });

  // Safe wrapper for setIsApiCall since it might be undefined from props
  const safeSetIsApiCall = useCallback(
    (value: boolean) => {
      if (setIsApiCall) {
        setIsApiCall(value);
      }
    },
    [setIsApiCall]
  );

  // Safe wrappers for other optional props
  const safeSetUpdateData = useCallback(
    (data: any) => {
      if (setUpdateData) {
        setUpdateData(data);
      }
    },
    [setUpdateData]
  );

  const safeSetIsPreview = useCallback(
    (value: boolean) => {
      if (setIsPreview) {
        setIsPreview(value);
      }
    },
    [setIsPreview]
  );

  const safeSetIsPreviewId = useCallback(
    (value: string) => {
      if (setIsPreviewId) {
        setIsPreviewId(value);
      }
    },
    [setIsPreviewId]
  );

  const safeSetHandleDrawerSize = useCallback(
    (value: boolean | number) => {
      if (setHandleDrawerSize) {
        // Convert boolean to number if needed (true -> 1, false -> 0)
        const numValue = typeof value === "boolean" ? (value ? 1 : 0) : value;
        setHandleDrawerSize(numValue);
      }
    },
    [setHandleDrawerSize]
  );

  const safeSetFinalPreview = useCallback(
    (value: boolean) => {
      if (setFinalPreview) {
        setFinalPreview(value);
      }
    },
    [setFinalPreview]
  );

  const safeSetFinalPreviewId = useCallback(
    (value: string) => {
      if (setFinalPreviewId) {
        setFinalPreviewId(value);
      }
    },
    [setFinalPreviewId]
  );

  const handleDebouncedChange = _debounce(() => {
    safeSetIsApiCall(true);
  }, 1500);

  // Fetch payment data when component mounts and show is true

  useEffect(() => {
    if (!show) {
      latestScheduleRequestRef.current += 1;
      setIsLoading(false);
      safeSetIsApiCall(false);
      return;
    }

    if (id) {
      hasUserEditedSchedule.current = false;
      hydratedPlanId.current = "";
      initialScheduleSnapshotRef.current = null;
      initialPaymentMethodRef.current = null;
      setUserPaymentMethodSelection(null);
      latestScheduleRequestRef.current += 1;
      safeSetIsApiCall(false);
      paymentPlanStateHooks.onGetPaymentData(id);
    }
  }, [id, show, safeSetIsApiCall]);

  // Fetch old card data when component mounts
  useEffect(() => {
    if (userData && show && isLogin) {
      const payload = {
        accountid: userData?.data?.accountData?.accountid,
        accounts_id: userData?.data?.accountData?.id,
        cluster_id: clusterId,
      };

      paymentPlanStateHooks.onOldCardDataGet(payload);
    }
  }, [userData, isLogin, show, clusterId]);

  // The blank-date warning is raised on Next, but the schedule can be fixed
  // without another recalculation, so clear it as soon as every row has a date.
  useEffect(() => {
    if (!isEndDateError) return;
    const missingDateError = getMissingScheduleDateError(
      data?.paymentPlan?.transactions
    );
    if (!missingDateError && dateErrMsg === MISSING_SCHEDULE_DATE_MESSAGE) {
      setIsEndDateError(false);
      setDateErrMsg(null);
    }
  }, [data?.paymentPlan?.transactions, isEndDateError, dateErrMsg]);

  useEffect(() => {
    const reduxScheduleId = `${paymentPlanDataRedux?.clientCustomerData?.id || ""}`;
    const currentScheduleId = `${decode(id || "")?.split("_")?.[0] || ""}`;

    if (
      paymentPlanDataRedux &&
      Object.keys(paymentPlanDataRedux).length > 0 &&
      show &&
      !hasUserEditedSchedule.current &&
      hydratedPlanId.current !== id &&
      (!currentScheduleId || reduxScheduleId === currentScheduleId)
    ) {
      const scheduleTransactions = normalizeUpdatePlanTransactions(
        paymentPlanDataRedux?.clinetScheduleCustomData || []
      );
      const initialTransactionType =
        scheduleTransactions[0]?.amount_type === "P" ? "percent" : "amount";
      const initialTotals = calculateScheduleSummary(
        scheduleTransactions,
        Number(
          paymentPlanDataRedux?.clientCustomerData?.initial_balance ??
            accountData?.balance ??
            0
        ),
        initialTransactionType
      );

      initialScheduleSnapshotRef.current = createPaymentPlanScheduleSnapshot({
        transactions: scheduleTransactions,
        no_of_transaction:
          paymentPlanDataRedux?.clinetScheduleCustomData?.length,
        payment_schedule:
          paymentPlanDataRedux?.clientCustomerData?.payment_schedule,
        schedule_fld:
          paymentPlanDataRedux?.clientCustomerData?.payment_schedule,
        schedule_amount:
          paymentPlanDataRedux?.clinetScheduleCustomData[0]?.schedule_type,
        transaction_type: initialTransactionType,
      });
      initialPaymentMethodRef.current = createPaymentMethodSelection(
        paymentPlanDataRedux?.clientCustomerData?.payment_method || "",
        paymentPlanDataRedux?.clientCustomerData?.cpp_id ??
          paymentPlanDataRedux?.clientCustomerData?.cppid
      );
      setUserPaymentMethodSelection(null);

      setData((prevData: any) => ({
        ...prevData,
        paymentPlan: {
          ...prevData.paymentPlan,
          transactions: scheduleTransactions,
          grand_total: initialTotals.grandTotal,
          amount_subtotal: initialTotals.amountSubtotal,
          fee_subtotal: initialTotals.feeSubtotal,
          payment_schedule:
            paymentPlanDataRedux?.clientCustomerData?.payment_schedule,
          no_of_transaction:
            paymentPlanDataRedux?.clinetScheduleCustomData?.length,
          start_date:
            paymentPlanDataRedux?.clinetScheduleCustomData[0]?.billing_date ??
            new Date(),
          amount:
            normalizeScheduleValue(
              paymentPlanDataRedux?.clinetScheduleCustomData[0]?.schedule_type
            ) === "fixed"
              ? getFixedScheduleInstallmentAmount(scheduleTransactions)
              : scheduleTransactions[0]?.amount,
          schedule_amount:
            paymentPlanDataRedux?.clinetScheduleCustomData[0]?.schedule_type,
          transaction_type: initialTransactionType,
          percentage_amounts: initialTotals.percentageAmounts,
          schedule_fld:
            paymentPlanDataRedux?.clientCustomerData?.payment_schedule,
        },
      }));
      hydratedPlanId.current = id;
    }
  }, [paymentPlanDataRedux, show, id]);

  const handleScheduleDataChange = useCallback((value: any) => {
    hasUserEditedSchedule.current = true;
    setData(value);
  }, []);

  const getPaymentPlanUpdateFields = (
    paymentMethod: string | undefined,
    sourceData: any = data,
    flags: PaymentPlanUpdateFlags = currentUpdateFlags
  ) =>
    createPaymentPlanUpdateFields({
      paymentPlan: sourceData?.paymentPlan,
      clusterid: sourceData?.clusterid,
      leadOwner: sourceData?.lead_owner,
      paymentMethod,
      flags,
    });

  const applyAuthoritativeUpdateResponse = (
    responseData: any,
    flags: PaymentPlanUpdateFlags = currentUpdateFlags
  ) => {
    if (!Array.isArray(responseData?.date_array)) return;

    setData((currentData: any) => {
      const totalBalance = Number(
        currentData?.clientCustomerData?.initial_balance ??
          currentData?.customerData?.initial_balance ??
          currentData?.customerData?.balance ??
          accountData?.balance ??
          0
      );
      const paymentPlan = getAuthoritativePaymentPlanState(
        currentData?.paymentPlan,
        responseData,
        totalBalance
      );

      initialScheduleSnapshotRef.current =
        createPaymentPlanScheduleSnapshot(paymentPlan);
      hasUserEditedSchedule.current = false;

      return {
        ...currentData,
        paymentPlan,
        clientCustomerData: {
          ...currentData?.clientCustomerData,
          remaining_payments: paymentPlan.remaining_payments,
          next_billing_date: paymentPlan.next_billing_date,
          amount_subtotal: paymentPlan.amount_subtotal,
          fee_subtotal: paymentPlan.fee_subtotal,
          grand_total: paymentPlan.grand_total,
        },
      };
    });

    if (flags.updateMethod) {
      initialPaymentMethodRef.current =
        userPaymentMethodSelection ||
        createPaymentMethodSelection(
          responseData?.payment_method || responseData?.x_payment_method || "",
          responseData?.cpp_id ?? responseData?.cppid
        );
      setUserPaymentMethodSelection(null);
    }
  };

  const dispatchPaymentPlanUpdate = async (payload: any) => {
    return runPaymentPlanUpdateOnce(updateRequestInFlightRef, async () => {
      setEmailDeliveryWarning(null);
      const updateResponse: any = await dispatch(
        updatePayment(payload)
      ).unwrap();
      const emailOutcome = await sendScheduleUpdatedEmailAfterUpdate({
        updateResponse,
        accountsId: userData?.data?.accountData?.id,
        clusterId: userData?.data?.accountData?.clusterid,
        fallbackScheduleId: paymentPlanDataRedux?.clientCustomerData?.id,
        notificationOutcomes: emailNotificationOutcomesRef.current,
        sendEmail: (emailPayload) =>
          dispatch(sendPaymentPlanUpdatedEmail(emailPayload)).unwrap(),
      });

      setEmailDeliveryWarning(emailOutcome.warning);
      return emailOutcome.updateResponse;
    });
  };

  useEffect(() => {
    if (!isApiCall || !show) return;

    const activeRequestId = latestScheduleRequestRef.current + 1;
    latestScheduleRequestRef.current = activeRequestId;

    const scheduleAmountType = getScheduleAmountType(data.paymentPlan);
    const transactionInputType = getTransactionInputType(data.paymentPlan);
    const amountTypeCode = getAmountTypeCode(data.paymentPlan);

    // calculate Amount if Auto selected
    let paymentAmount, currentBalance;
    const totalTransactions = getScheduleTransactionCount(data.paymentPlan);

    if (scheduleAmountType === "auto") {
      currentBalance =
        pendingBalance > 0 ? pendingBalance : data?.customerData?.balance;
      const avg = Number(
        averageAmount(currentBalance, data.paymentPlan?.no_of_transaction)
      );

      paymentAmount = avg;
    } else {
      currentBalance = data?.customerData?.balance;
      paymentAmount = `${data.paymentPlan?.amount}`?.replaceAll(",", "");
    }

    const paymentMethod = getPaymentMethodCode(selectedMethod);

    console.log("paymentMethod:-->", paymentMethod, selectedMethod);
    let tmpTransactions = Array.from(data.paymentPlan.transactions);
    tmpTransactions = tmpTransactions?.map((transaction: any) => ({
      amount: `${transaction?.amount}`,
      amount_type: transaction?.amount_type,
      // balance: 13988.14,
      fee: getTransactionFeeForPaymentMethod(transaction, paymentMethod),
      payment_date: getTransactionScheduleDate(transaction),
      status: transaction?.status,
    }));
    const payload = {
      date_array: tmpTransactions.slice(0, totalTransactions),
      lead_id: accountLeadData?.lead_id,
      cluster_id: accountData?.clusterid,
      balance: currentBalance,
      schedule_amount: scheduleAmountType,
      no_of_transaction: totalTransactions,
      grand_total: data.paymentPlan.grand_total,
      fee_subtotal: data.paymentPlan.fee_subtotal,
      amount_total: data.paymentPlan.amount_subtotal,
      isAmount: true,
      isDelete: false,
      start_date: getPaymentPlanStartDate(data.paymentPlan, startDate),
      schedule_fld: data.paymentPlan?.schedule_fld,
      amount: paymentAmount,
      transaction_type: transactionInputType,
      custom_conv: "on",
      make_payment: true,
      payment_method: paymentMethod,
      state: userData?.data?.accountData?.state || "",
      shouldCallCustom: true,
    };

    setIsLoading(true);

    dispatch(getPaymentPlanDates(payload))
      .unwrap()
      .then((res: any) => {
        if (
          !isLatestScheduleRequest(
            activeRequestId,
            latestScheduleRequestRef.current
          )
        )
          return;

        const { data: planData, success } = res?.body || {};
        const endDateError = getScheduleEndDateError(res?.body);
        setIsEndDateError(endDateError.isInvalid);
        setDateErrMsg(endDateError.message);

        if (success) {
          const responseTransactions = Array.isArray(planData?.date_array)
            ? planData.date_array
            : [];
          const targetTransactionCount = getScheduleTransactionCount(
            data.paymentPlan
          );
          const preserveCustomDates =
            normalizeScheduleValue(data.paymentPlan?.schedule_fld) ===
              "custom" && !endDateError.isInvalid;
          const newTransactionArr = reconcileUpdatePlanScheduleTransactions(
            data?.paymentPlan?.transactions || [],
            responseTransactions,
            targetTransactionCount,
            {
              amountTypeCode,
              preserveCustomDates,
              scheduleAmountType,
            }
          );

          const sortedTransactionArr = normalizeScheduleFeesForPaymentMethod(
            sortDates<Transaction>(newTransactionArr as Transaction[]),
            paymentMethod
          );
          const scheduleBalance = Number(
            data?.clientCustomerData?.initial_balance ??
              data?.customerData?.initial_balance ??
              data?.customerData?.balance ??
              accountData?.balance ??
              0
          );
          const calculatedData = calculateScheduleSummary(
            sortedTransactionArr,
            scheduleBalance,
            transactionInputType
          );

          const payment = {
            ...data.paymentPlan,
            transactions: calculatedData.dataArr,
            grand_total: calculatedData.grandTotal,
            amount_subtotal: calculatedData.amountSubtotal,
            fee_subtotal: calculatedData.feeSubtotal,
            percentage_amounts: calculatedData.percentageAmounts,
            no_of_transaction: targetTransactionCount,
            transaction_type: transactionInputType,
          };
          setData((state: any) => ({ ...state, paymentPlan: payment }));
          const missingDateError = getMissingScheduleDateError(
            calculatedData.dataArr
          );
          if (!endDateError.isInvalid && missingDateError) {
            setIsEndDateError(true);
            setDateErrMsg(missingDateError);
          }
          paymentPlanBeforeAddRef.current = null;
        } else if (paymentPlanBeforeAddRef.current) {
          const previousPaymentPlan = paymentPlanBeforeAddRef.current;
          setData((state: any) => ({
            ...state,
            paymentPlan: previousPaymentPlan,
          }));
          paymentPlanBeforeAddRef.current = null;
        } else if (!endDateError.isInvalid) {
          setIsEndDateError(true);
          setDateErrMsg(
            "We couldn't recalculate the payment schedule. Please try again."
          );
        }
      })
      .catch(() => {
        if (
          !isLatestScheduleRequest(
            activeRequestId,
            latestScheduleRequestRef.current
          )
        )
          return;

        if (paymentPlanBeforeAddRef.current) {
          const previousPaymentPlan = paymentPlanBeforeAddRef.current;
          setData((state: any) => ({
            ...state,
            paymentPlan: previousPaymentPlan,
          }));
        }
        paymentPlanBeforeAddRef.current = null;
        setIsEndDateError(true);
        setDateErrMsg(
          "We couldn't recalculate the payment schedule. Please try again."
        );
      })
      .finally(() => {
        if (
          !isLatestScheduleRequest(
            activeRequestId,
            latestScheduleRequestRef.current
          )
        )
          return;

        safeSetIsApiCall(false);
        setIsLoading(false);
      });
  }, [
    isApiCall,
    show,
    selectedMethod,
    data,
    pendingBalance,
    disabledCount,
    startDate,
    accountLeadData?.lead_id,
    accountData?.clusterid,
    dispatch,
    safeSetIsApiCall,
  ]);

  const addNewRow = () => {
    if (isLoading) return;

    hasUserEditedSchedule.current = true;
    paymentPlanBeforeAddRef.current = {
      ...data.paymentPlan,
      transactions: [...(data.paymentPlan?.transactions || [])],
      percentage_amounts: [...(data.paymentPlan?.percentage_amounts || [])],
    };
    const scheduleAmountType = getScheduleAmountType(data.paymentPlan);
    const amountTypeCode = getAmountTypeCode(data.paymentPlan);

    if (scheduleAmountType === "fixed") {
      const tmpTransactions = Array.from(data.paymentPlan.transactions);

      let sum = 0;
      let currBalance =
        pendingBalance > 0 ? pendingBalance : data?.customerData?.balance;

      let confee = 0;
      let fee = 0;
      tmpTransactions?.forEach((transaction: any) => {
        if (Number(transaction.amount) === Number(data?.paymentPlan?.amount))
          confee = getTransactionConvenienceFee(transaction);
      });

      tmpTransactions.push({
        amount: parseFloat(data?.paymentPlan?.amount).toFixed(2),
        amount_type: amountTypeCode,
        balance: currBalance,
        fee: confee,
        payment_date: null,
        status: "Pending",
      });

      const newArr = tmpTransactions?.map((transaction: any) => {
        currBalance -= Number(transaction?.amount);
        sum += Number(transaction?.amount);
        fee += getTransactionConvenienceFee(transaction);
        return { ...transaction, balance: currBalance };
      });

      setData((data: any) => ({
        ...data,
        paymentPlan: {
          ...data.paymentPlan,
          transactions: newArr,
          amount_subtotal: sum,
          fee_subtotal: fee,
          grand_total: sum + fee,
          no_of_transaction: newArr?.length,
        },
      }));
    } else if (scheduleAmountType === "auto") {
      const scheduleBalance = Number(
        data?.clientCustomerData?.initial_balance ??
          data?.customerData?.initial_balance ??
          data?.customerData?.balance ??
          accountData?.balance ??
          0
      );
      const tmpTransactions: Transaction[] = [
        ...data.paymentPlan.transactions,
        {
          amount: "0.00",
          amount_type: amountTypeCode,
          balance: scheduleBalance,
          fee: 0,
          payment_date: null,
          status: "Pending",
        },
      ];
      const newArr = redistributePendingScheduleAmounts(
        tmpTransactions,
        scheduleBalance
      );
      const totals = calculateScheduleSummary(
        newArr,
        scheduleBalance,
        "amount"
      );

      setData((data: any) => ({
        ...data,
        paymentPlan: {
          ...data.paymentPlan,
          transactions: totals.dataArr,
          amount_subtotal: totals.amountSubtotal,
          fee_subtotal: totals.feeSubtotal,
          grand_total: totals.grandTotal,
          no_of_transaction: newArr?.length,
        },
      }));
    } else {
      const tmpTransactions = Array.from(data.paymentPlan.transactions);
      const amountArr = Array.from(data.paymentPlan?.percentage_amounts || []);

      let sum = 0;
      let fee = 0;
      let currBalance =
        pendingBalance > 0 ? pendingBalance : data?.customerData?.balance;

      tmpTransactions.push({
        amount: "0.00",
        amount_type: amountTypeCode,
        balance: currBalance,
        fee: 0,
        payment_date: null,
        status: "Pending",
      });

      if (getTransactionInputType(data.paymentPlan) === "percent") {
        const calculatedData = CalculateCustomPercentage(
          tmpTransactions,
          0,
          0,
          pendingBalance > 0 ? pendingBalance : data?.customerData?.balance
        );

        const plan = {
          ...data.paymentPlan,
          transactions: calculatedData.dataArr,
          percentage_amounts: calculatedData.amountArray,
          grand_total:
            Number(calculatedData.totalAmount) +
            Number(calculatedData.totalFee),
          amount_subtotal: calculatedData.totalAmount,
          fee_subtotal: calculatedData.totalFee,
          no_of_transaction: calculatedData.dataArr.length,
        };

        setData((state: any) => ({
          ...state,
          paymentPlan: plan,
        }));
      } else {
        const newArr = tmpTransactions?.map((transaction: any, idx: number) => {
          currBalance -= Number(transaction?.amount);

          const toAdd =
            transaction?.amount_type === "P" && amountArr?.length > 0
              ? amountArr[idx]
              : transaction?.amount;

          sum += Number(toAdd);
          fee += getTransactionConvenienceFee(transaction);
          return { ...transaction, balance: currBalance };
        });

        setData((data: any) => ({
          ...data,
          paymentPlan: {
            ...data.paymentPlan,
            transactions: newArr,
            amount_subtotal: sum,
            fee_subtotal: fee,
            grand_total: sum + fee,
            no_of_transaction: newArr?.length,
          },
        }));
      }
    }

    setTimeout(() => {
      safeSetIsApiCall(true);
    }, 0);
  };

  const removeRow = (index: number) => {
    hasUserEditedSchedule.current = true;
    if (getScheduleAmountType(data.paymentPlan) === "auto") {
      const tmpTransactions: Transaction[] = Array.from(
        data.paymentPlan.transactions
      );
      tmpTransactions.splice(index, 1);
      const scheduleBalance = Number(
        data?.clientCustomerData?.initial_balance ??
          data?.customerData?.initial_balance ??
          data?.customerData?.balance ??
          accountData?.balance ??
          0
      );
      const newArr = redistributePendingScheduleAmounts(
        tmpTransactions,
        scheduleBalance
      );
      const totals = calculateScheduleSummary(
        newArr,
        scheduleBalance,
        "amount"
      );

      setData((data: any) => ({
        ...data,
        paymentPlan: {
          ...data.paymentPlan,
          transactions: totals.dataArr,
          amount_subtotal: totals.amountSubtotal,
          fee_subtotal: totals.feeSubtotal,
          grand_total: totals.grandTotal,
          no_of_transaction: newArr.length,
        },
      }));
    } else {
      const tmpTransactions = Array.from(data.paymentPlan.transactions);
      tmpTransactions.splice(index, 1);

      if (getTransactionInputType(data.paymentPlan) === "percent") {
        const calculatedData = CalculateCustomPercentage(
          tmpTransactions,
          0,
          0,
          pendingBalance > 0 ? pendingBalance : data?.customerData?.balance
        );

        const plan = {
          ...data.paymentPlan,
          transactions: calculatedData.dataArr,
          percentage_amounts: calculatedData.amountArray,
          grand_total:
            Number(calculatedData.totalAmount) +
            Number(calculatedData.totalFee),
          amount_subtotal: calculatedData.totalAmount,
          fee_subtotal: calculatedData.totalFee,
          no_of_transaction: calculatedData.dataArr.length,
        };

        setData((state: any) => ({
          ...state,
          paymentPlan: plan,
        }));
      } else {
        let currBalance =
          pendingBalance > 0 ? pendingBalance : data?.customerData?.balance;
        let sum = 0;
        let fee = 0;

        const newArr = tmpTransactions?.map((transaction: any) => {
          currBalance -= Number(transaction?.amount);
          sum += Number(transaction?.amount);
          fee += getTransactionConvenienceFee(transaction);
          return { ...transaction, balance: currBalance };
        });
        setData((data: any) => ({
          ...data,
          paymentPlan: {
            ...data.paymentPlan,
            grand_total: sum + fee,
            amount_subtotal: sum,
            fee_subtotal: fee,
            transactions: newArr,
            no_of_transaction: newArr.length,
          },
        }));
      }
    }
  };

  const insertInOrder = (array: any, newNumber: any) => {
    const dummyArr: any = JSON.parse(JSON.stringify(array));
    const index = dummyArr.findIndex((val: any) => newNumber < val);
    if (index === -1) {
      dummyArr.push(newNumber);
    } else {
      dummyArr.splice(index, 0, newNumber);
    }
    return dummyArr;
  };

  const calculateTotal = (balance?: number, selectedOffer?: any): number => {
    if (balance === undefined || selectedOffer === undefined)
      return balance || 0;

    const settlementAmount = parseFloat(
      selectedOffer.settlement_amount as string
    );
    if (selectedOffer.settlement_offer === "F") {
      return balance - settlementAmount;
    } else if (selectedOffer.settlement_offer === "P") {
      return balance - balance * (settlementAmount / 100);
    } else {
      return balance;
    }
  };

  const handleChange = (type: HandleChangeType, index: number, value: any) => {
    hasUserEditedSchedule.current = true;
    let tmpPaymentPlan: PaymentPlan = { ...data.paymentPlan };
    let tmpTransactionArr: Transaction[] = tmpPaymentPlan.transactions.map(
      (t) => ({ ...t })
    );
    let plan = {};

    switch (type) {
      case "date":
        tmpTransactionArr[index] = updateTransactionScheduleDate(
          tmpTransactionArr[index],
          value
        );

        const sorted = sortDates(tmpTransactionArr);

        const scheduleBalance = Number(
          data?.clientCustomerData?.initial_balance ??
            data?.customerData?.initial_balance ??
            data?.customerData?.balance ??
            accountData?.balance ??
            0
        );
        const calculatedData = calculateScheduleSummary(
          sorted,
          scheduleBalance,
          getTransactionInputType(tmpPaymentPlan)
        );

        setData((state: any) => ({
          ...state,
          paymentPlan: {
            ...tmpPaymentPlan,
            transactions: calculatedData.dataArr,
            amount_subtotal: calculatedData.amountSubtotal,
            fee_subtotal: calculatedData.feeSubtotal,
            grand_total: calculatedData.grandTotal,
            percentage_amounts: calculatedData.percentageAmounts,
          },
        }));
        break;
      case "amount":
        tmpTransactionArr[index].amount = value !== 0 ? Number(value) : "";

        if (value?.toString()) {
          const calculatedData = calculateAmountOutstanding(
            tmpTransactionArr,
            pendingBalance > 0 ? pendingBalance : data?.customerData?.balance
          );
          plan = {
            ...tmpPaymentPlan,
            transactions: calculatedData.dataArr,
            grand_total: calculatedData.totalAmount + calculatedData.totalFee,
            amount_subtotal: calculatedData.totalAmount,
            fee_subtotal: calculatedData.totalFee,
          };
        } else {
          plan = {
            ...tmpPaymentPlan,
            transactions: tmpTransactionArr,
          };
        }

        setData((state: any) => ({
          ...state,
          paymentPlan: plan,
        }));

        break;
      case "percent":
        tmpTransactionArr[index].amount = value !== 0 ? Number(value) : "";
        const totalPercentage = percentageCount(tmpTransactionArr);

        // check percentage limit
        if (totalPercentage > 100) return;
        if (value?.toString()) {
          const calculatedData = CalculateCustomPercentage(
            tmpTransactionArr,
            index,
            value,
            pendingBalance > 0 ? pendingBalance : data?.customerData?.balance
          );

          plan = {
            ...tmpPaymentPlan,
            transactions: calculatedData.dataArr,
            percentage_amounts: calculatedData.amountArray,
            grand_total:
              Number(calculatedData.totalAmount) +
              Number(calculatedData.totalFee),
            amount_subtotal: calculatedData.totalAmount,
          };
        } else {
          plan = {
            ...tmpPaymentPlan,
            transactions: tmpTransactionArr,
          };
        }

        setData((state: any) => ({
          ...state,
          paymentPlan: plan,
        }));

        break;
    }

    if (
      !updatedArr.createdValue.includes(index) &&
      !updatedArr.updatedValue.includes(index)
    ) {
      setUpdatedArr((state: any) => ({
        ...state,
        updatedValue: insertInOrder(state.updatedValue, index),
      }));
    }
  };

  const sendEmail = async ({ shouldSendAll, ...values }: any) => {
    try {
      const fallbackEmail = getPrimaryEmail(data, userData, emailDisplay);
      const emails = Array.from(
        new Set(
          (Array.isArray(values.emails) ? values.emails : [values.emails])
            .filter(Boolean)
            .map((email: string) => `${email}`.trim())
            .filter(validateEmail)
        )
      );

      if (emails.length === 0 && validateEmail(fallbackEmail)) {
        emails.push(fallbackEmail);
      }

      // if ((!values.invoice_id) || emails.length === 0)
      return;

      let payload: any = {
        accountid: userData?.data?.accountData?.accountid,
        emails,
        shouldSendAll: shouldSendAll,
      };

      if (values.schedule_id) {
        payload = { ...payload, schedule_id: values.schedule_id };
      } else if (values.invoice_id) {
        payload = { ...payload, invoice_id: values.invoice_id };
      }
      await dispatch(paymentDownload(payload)).unwrap();
    } catch (error) {
      console.log("sendEmail", error);
    }
  };

  const getAdressValue = (payload: any) => {
    if (payload?.x_payment_method === "CC") {
      return getUpdateBillingAddress(
        payload,
        undefined,
        paymentPlanDataRedux?.clientCustomerData,
        accountData
      );
    } else {
      return {
        x_city: accountData?.city,
        x_address: accountData?.address1,
        address2: accountData?.address2,
        x_zip: accountData?.postal_code,
        x_state: accountData?.state,
        x_country: accountData?.country,
      };
    }
  };

  const createPayload = (values: any) => {
    const { x_city, x_address, address2, x_zip, x_state, x_country } =
      getAdressValue(values);
    let method = getMethod(isAddNewCard, dataPayment, selectedMethod);
    return {
      x_card_num: values?.x_card_num?.replaceAll(" ", ""),
      x_exp_date: values?.x_exp_date,
      x_card_code: values?.x_card_code,
      choldername: values?.choldername,
      x_payment_method: method,
      x_city,
      x_address,
      address2,
      x_zip,
      x_state,
      x_country,
      routing_number: values?.routing_number,
      account_number: values?.account_number,
      bank_name: values?.bankName,
      swift_code: values?.swift_code,
      card_funding_type: values?.card_funding_type,
      // default: checkDefaultMethodExist(cardList, bankList, oldWalletData),
      // default_profile: checkDefaultMethodExist(
      //   cardList,
      //   bankList,
      //   oldWalletData
      // ),
      default: "Y",
      default_profile: "N",
      cluster_id: accountData?.clusterid,
      accountid: accountData?.accountid,
      x_first_name: accountData?.first_name,
      x_last_name: accountData?.last_name,
      x_phone: accountData?.phone || "",
      x_email: accountData?.email || "",
      lead_id: accountLeadData?.lead_id || "",
      gateway_id:
        method === "ACH"
          ? paymentPermission?.ach_gateway
          : paymentPermission?.cc_gateway,
      gateway_name:
        method === "ACH"
          ? paymentPermission?.ach_gateway_name?.name
          : paymentPermission?.cc_gateway_name?.name,
      account_holder_name: values?.choldername ?? "",
      card_type: values?.iconType,
      accounts_id: accountData?.id,
    };
  };

  const handleCreateNewCard = async (payload: any, isRetry: any) => {
    try {
      let payloads = createPayload(payload);
      let res: any = await dispatch(addCard(payloads)).unwrap();
      if (res?.body?.success) {
        const paymentMethod = getMethod(
          isAddNewCard,
          dataPayment,
          selectedMethod
        );
        const updateBillingAddress = getUpdateBillingAddress(
          payloads,
          res?.body?.data?.paymentMethod,
          paymentPlanDataRedux?.clientCustomerData,
          accountData
        );

        const payment_payload = {
          schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
          ...getPaymentPlanUpdateFields(paymentMethod),
          accounts_id: userData?.data?.accountData?.accounts_id || "",
          user_id: userData?.data?.accountData?.leadData?.user_id || "",
          x_payment_method: paymentMethod || "",
          cc_token: res?.body?.data?.paymentMethod?.cc_token || "",
          cc_token2: res?.body?.data?.paymentMethod?.cc_token2 || "",
          cppid: res?.body?.data?.paymentMethod?.id || "",
          isRetry: isRetry ? true : false || "",
          routing_number: payload?.routing_number || "",
          account_number: payload?.account_number || "",
          bank_name: values?.bank_name || "",
          holdername: payload?.choldername || "",
          choldername: payload?.choldername || "",
          aholdername: payload?.choldername || "",
          balance: userData?.data?.accountData?.balance,
          bank_address:
            paymentPlanDataRedux?.clientCustomerData?.bank_address || "",
          cc_expiry: payload?.x_exp_date || "",
          cc_cvv: payload?.x_card_code || "",
          cc_type: payload?.card_type || "",
          cc_4digit: res?.body?.data?.paymentMethod?.cc_4digit || "",
          accountid: userData?.data?.accountData?.accountid || "",
          customer_device: getOperatingSystem() || "",
          ...updateBillingAddress,
          use_old_card: "1",
          clusterid: res?.body?.data?.paymentMethod?.clusterid || "",
          is_3ds: false,
        };

        const response: any = await dispatchPaymentPlanUpdate(payment_payload);
        if (!response) return;

        const failure = getUpdatePlanFailure(response?.body);
        if (!failure) {
          applyAuthoritativeUpdateResponse(response?.body?.data);
          let email = [];
          email.push(dataPayment?.email);
          let values = {
            emails: email,
            schedule_id: response?.body?.data?.schedule_id,
          };
          // await sendEmail(values);

          setIsUpdatePaymentPlan(true);
          setPlanSuccess(true);
          setPaymentScheduleId(response?.body?.data?.schedule_id);
        } else {
          showPlanUpdateFailure(response?.body);
        }
      } else {
        showPlanUpdateFailure(res?.body);
      }
    } catch (error: any) {
      showPlanUpdateFailure(undefined, error);
    } finally {
      setIsUpdateLoading(false);
      setIsUpdateRetryLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const paymentViaWire = async (isRetry: any) => {
    let slug = decode(id || "")?.split("_");
    try {
      let payload = {
        payment_gateway_id: "",
        amount: parseFloat(data?.paymentPlan.amount).toFixed(2),
        clusterid: userData?.data?.accountData?.clusterid,
        x_method: "WTO",
        lead_id: accountLeadData?.lead_id,
        email: userData?.data?.accountData?.email,
        accountid: userData?.data?.accountData?.accountid,
        payment_link: slug[0],
        description: "Online Payment",
        payment_note: "Custom Plan",
      };

      let res: any = await dispatch(wirePaymentProcess(payload)).unwrap();
      if (res.body.success) {
        setWireDetails({
          email: res?.body?.data?.email || "",
          bank: res?.body?.data?.bank_name || "",
          routing: res?.body?.data?.routing_number || "",
          account: res?.body?.data?.account_number || "",
          swift: res?.body?.data?.swift_code || "",
          dueDate:
            moment(
              moment().add(res?.body?.data?.wire_deadline || 0, "days")
            ).format("MM/DD/YYYY") || "",
        });
        setInvoiceId(res?.body?.data?.invoice_id);
        setIsUpdatePaymentPlan(true);
        setIsWirePayment(true);
        setIsUpdateRetryLoading(false);
        setIsUpdateLoading(false);
        setPlanSuccess(true);
        const payment_payload = {
          schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
          ...getPaymentPlanUpdateFields(
            getMethod(isAddNewCard, dataPayment, selectedMethod)
          ),
          accounts_id: userData?.data?.accountData?.accounts_id || "",
          user_id: userData?.data?.accountData?.leadData?.user_id || "",
          x_payment_method:
            getMethod(isAddNewCard, dataPayment, selectedMethod) || "",
          cc_token: dataPayment?.selectedCard
            ? dataPayment?.selectedCard?.cc_token
            : dataPayment?.selectedBank?.cc_token || "",
          cc_token2: dataPayment?.selectedCard
            ? dataPayment?.selectedCard?.cc_token2
            : dataPayment?.selectedBank?.cc_token2 || "",
          cppid: dataPayment?.selectedCard?.id
            ? dataPayment?.selectedCard?.id
            : paymentPlanDataRedux?.clientCustomerData?.cpp_id || "",
          isRetry: isRetry || false,
          routing_number: dataPayment?.selectedBank?.routing_number || "",
          account_number: dataPayment?.selectedBank?.account_number || "",
          bank_name: dataPayment?.selectedBank?.bank_name || "",
          choldername: paymentPlanDataRedux?.clientCustomerData?.cc_name || "",
          bank_address: "",
          cc_expiry: dataPayment?.selectedCard?.cc_expiry || "",
          cc_cvv: dataPayment?.selectedCard?.cc_cvv || "",
          cc_type: dataPayment?.selectedCard?.cc_type || "",
          cc_4digit: dataPayment?.selectedCard?.cc_4digit || "",
          accountid: userData?.data?.accountData?.accountid || "",
          customer_device: getOperatingSystem(),
          clusterid: userData?.data?.accountData?.clusterid,
          x_email:
            paymentPlanDataRedux?.clientCustomerData?.email ||
            userData?.data?.accountData?.email ||
            "",
        };

        setPaymentScheduleId(paymentPlanDataRedux?.clientCustomerData?.id);
        try {
          const response: any =
            await dispatchPaymentPlanUpdate(payment_payload);
          if (!response) return;
          if (response?.body?.success) {
            applyAuthoritativeUpdateResponse(response?.body?.data);
            let email = [];
            email.push(dataPayment?.email);
            let values = {
              emails: res?.body?.data?.email,
              schedule_id: paymentPlanDataRedux?.clientCustomerData?.id,
            };
            // await sendEmail(values);
          }
        } catch (error) {
          setIsUpdateRetryLoading(false);
          setIsUpdateLoading(false);
        }
      } else {
        setIsUpdateRetryLoading(false);
        setIsUpdateLoading(false);
        setIsUpdatePaymentPlan(false);
        setPlanSuccess(false);
      }
    } catch (error) {
      setIsUpdateRetryLoading(false);
      setIsUpdateLoading(false);
      setIsUpdatePaymentPlan(false);
      setPlanSuccess(false);
      console.log("paymentViaWire error =====", error);
      return false;
    }
  };

  const handlePayPalPayment = async (tokendata: any) => {
    try {
      let res: any = await dispatch(
        createPaypalPaymentToken(tokendata)
      ).unwrap();
      const data = payPalOrder.data;
      let dataPayment = data?.paymentPlan;
      if (res?.body?.success) {
        const redirectFlags: PaymentPlanUpdateFlags = {
          updateMethod: parseUpdateFlag(
            payPalOrder?.updateFlags?.updateMethod ?? true
          ),
          updateSchedule: parseUpdateFlag(
            payPalOrder?.updateFlags?.updateSchedule
          ),
        };

        const payment_payload = {
          schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
          ...getPaymentPlanUpdateFields("PP", data, redirectFlags),
          accounts_id: userData?.data?.accountData?.accounts_id || "",
          user_id: userData?.data?.accountData?.leadData?.user_id || "",
          x_payment_method: "PP",
          cc_token: res?.body?.data?.cc_token || "",
          cppid: res?.body?.data?.cppid || "",
          isRetry: payPalOrder?.isRetry ? true : false || "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          choldername: "",
          aholdername: "",
          holdername: "",
          bank_address: "",
          cc_expiry: "",
          cc_cvv: "",
          cc_type: "",
          cc_4digit: "",
          accountid: userData?.data?.accountData?.accountid || "",
          customer_device: getOperatingSystem(),
          clusterid: userData?.data?.accountData?.clusterid,
        };

        if (payPalOrder?.isRetry) {
          setIsUpdateRetryLoading(true);
        } else {
          setIsUpdateLoading(true);
        }

        const response: any = await dispatchPaymentPlanUpdate(payment_payload);
        if (!response) return;
        if (response?.body?.success) {
          applyAuthoritativeUpdateResponse(response?.body?.data, redirectFlags);
          let email = [];
          email.push(dataPayment?.email);
          let values = {
            emails: email,
            schedule_id: response?.body?.data?.schedule_id,
          };
          // await sendEmail(values);
          setPaymentScheduleId(response?.body?.data?.schedule_id);
          setIsUpdatePaymentPlan(true);
          setPlanSuccess(true);
        } else {
          setIsUpdatePaymentPlan(true);
          setPlanSuccess(false);
        }
      } else {
        setIsUpdatePaymentPlan(true);
        setPlanSuccess(false);
      }
    } catch (error) {
      console.log("handlePayPalPayment error =====", error);

      setIsUpdatePaymentPlan(true);
      setPlanSuccess(false);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      setIsScreenLoading(false);
      setIsUpdateRetryLoading(false);
      setIsUpdateLoading(false);
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
  };

  const handleVerifyPayment = async (value: any) => {
    try {
      let dataPayment = verifyPaymentPlan;
      const redirectFlags: PaymentPlanUpdateFlags = {
        updateMethod: parseUpdateFlag(value?.updateMethod),
        updateSchedule: parseUpdateFlag(value?.updateSchedule),
      };

      const payment_payload = {
        schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
        ...getPaymentPlanUpdateFields(
          value?.method,
          { ...data, paymentPlan: dataPayment },
          redirectFlags
        ),
        accounts_id: userData?.data?.accountData?.accounts_id || "",
        user_id: userData?.data?.accountData?.leadData?.user_id || "",
        x_payment_method: value?.method,
        cc_token: "",
        cppid: "",
        isRetry: parseUpdateFlag(value?.isRetry ?? value?.updateMethod),
        routing_number: "",
        account_number: "",
        bank_name: "",
        holdername: paymentPlanDataRedux?.clientCustomerData?.cc_name || "",
        bank_address: "",
        cc_expiry: "",
        cc_cvv: "",
        cc_type: "",
        cc_4digit: "",
        accountid: userData?.data?.accountData?.accountid || "",
        customer_device: getOperatingSystem(),
        clusterid: userData?.data?.accountData?.clusterid,
        transaction_id: value.intent,
        is_3ds: value?.method === "CC" ? true : false,
      };
      const response: any = await dispatchPaymentPlanUpdate(payment_payload);
      if (!response) return;
      if (response?.body?.success) {
        applyAuthoritativeUpdateResponse(response?.body?.data, redirectFlags);
        let email = [];
        email.push(dataPayment?.email);
        let values = {
          emails: email,
          schedule_id: response?.body?.data?.schedule_id,
        };
        // await sendEmail(values);
        setPaymentScheduleId(response?.body?.data?.schedule_id);
        setIsUpdatePaymentPlan(true);
        setPlanSuccess(true);
      } else {
        setIsUpdatePaymentPlan(true);
        setPlanSuccess(false);
      }
    } catch (error) {
      console.log("handleVerifyPayment error =====", error);

      setIsUpdatePaymentPlan(true);
      setPlanSuccess(false);
    } finally {
      setIsUpdateRetryLoading(false);
      setIsUpdateLoading(false);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      setIsScreenLoading(false);
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
  };

  const handleApplePay = async (success: boolean, paymentIntent: any) => {
    try {
      if (success) {
        let dataPayment = data.paymentPlan;
        const walletFlags = { ...currentUpdateFlags, updateMethod: true };

        const payment_payload = {
          schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
          ...getPaymentPlanUpdateFields("AP", data, walletFlags),
          accounts_id: userData?.data?.accountData?.accounts_id || "",
          user_id: userData?.data?.accountData?.leadData?.user_id || "",
          x_payment_method: "AP",
          cc_token: "",
          cppid: "",
          isRetry: true,
          routing_number: dataPayment?.selectedBank?.routing_number || "",
          account_number: dataPayment?.selectedBank?.account_number || "",
          bank_name: dataPayment?.selectedBank?.bank_name || "",
          holdername: paymentPlanDataRedux?.clientCustomerData?.cc_name || "",
          bank_address: "",
          cc_expiry: dataPayment?.selectedCard?.cc_expiry || "",
          cc_cvv: dataPayment?.selectedCard?.cc_cvv || "",
          cc_type: dataPayment?.selectedCard?.cc_type || "",
          cc_4digit: dataPayment?.selectedCard?.cc_4digit || "",
          accountid: userData?.data?.accountData?.accountid || "",
          customer_device: getOperatingSystem(),
          clusterid: userData?.data?.accountData?.clusterid,
          transaction_id: paymentIntent,
          is_3ds: false,
        };
        const response: any = await dispatchPaymentPlanUpdate(payment_payload);
        if (!response) return;
        if (response?.body?.success) {
          applyAuthoritativeUpdateResponse(response?.body?.data, walletFlags);
          let email = [];
          email.push(dataPayment?.email);
          let values = {
            emails: email,
            schedule_id: response?.body?.data?.schedule_id,
          };
          // await sendEmail(values);
          setPaymentScheduleId(response?.body?.data?.schedule_id);
          setIsUpdatePaymentPlan(true);
          setPlanSuccess(true);
        } else {
          setFailedReason("Payment failed");
          setIsUpdatePaymentPlan(true);
          setPlanSuccess(false);
        }
      } else {
        setFailedReason("Payment failed");
        setIsUpdatePaymentPlan(true);
        setPlanSuccess(false);
      }
    } catch (error) {
      setFailedReason("Payment failed");
      setIsUpdatePaymentPlan(true);
      setPlanSuccess(false);
      console.log("error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      setIsUpdateLoading(false);
      setIsUpdateRetryLoading(false);
      setIsScreenLoading(false);
    }
  };

  const handleGooglePay = async (tokenData: any, total_amount: any) => {
    const { tokenizationData } = tokenData;
    let resp = JSON.parse(tokenizationData.token);
    try {
      setIsGoogleLoading(true);
      let dataPayment = data.paymentPlan;
      const walletFlags = { ...currentUpdateFlags, updateMethod: true };

      const payment_payload = {
        schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
        ...getPaymentPlanUpdateFields("GP", data, walletFlags),
        accounts_id: userData?.data?.accountData?.accounts_id || "",
        user_id: userData?.data?.accountData?.leadData?.user_id || "",
        x_payment_method: "GP",
        cc_token: "",
        cppid: "",
        isRetry: true,
        address1: resp?.card?.address_line1 || "",
        address2: resp?.card?.address_line2 || "",
        city: resp?.card?.address_city || "",
        state: resp?.card?.address_state || "",
        zip: resp?.card?.address_zip || "",
        country: resp?.card?.address_country || "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        holdername: paymentPlanDataRedux?.clientCustomerData?.cc_name || "",
        bank_address: "",
        cc_expiry: resp?.card?.exp_month + "/" + resp?.card?.exp_year || "",
        cc_cvv: "",
        cc_type: resp?.card?.brand || "",
        cc_4digit: resp?.card?.last4 || "",
        accountid: userData?.data?.accountData?.accountid || "",
        customer_device: getOperatingSystem(),
        clusterid: userData?.data?.accountData?.clusterid,
        stripe_token: resp?.id,
        is_3ds: false,
      };
      const response: any = await dispatchPaymentPlanUpdate(payment_payload);
      if (!response) return;
      if (response?.body?.success) {
        applyAuthoritativeUpdateResponse(response?.body?.data, walletFlags);
        let email = [];
        email.push(dataPayment?.email);
        let values = {
          emails: email,
          schedule_id: response?.body?.data?.schedule_id,
        };
        // sendEmail(values);
        setPaymentScheduleId(response?.body?.data?.schedule_id);
        setIsUpdatePaymentPlan(true);
        setPlanSuccess(true);
      } else {
        setFailedReason("Payment failed");
        setIsUpdatePaymentPlan(true);
        setPlanSuccess(false);
      }
    } catch (error) {
      setIsGoogleLoading(false);
      console.log("handleGooglePay error =====", error);
    } finally {
      setIsUpdateLoading(false);
      setIsUpdateRetryLoading(false);
    }
  };

  useEffect(() => {
    //paypal
    if (
      (searchParams.has("approval_session_id") ||
        searchParams.has("approval_token_id")) &&
      searchParams.has("plansDrawer") &&
      searchParams.has("planId") &&
      !searchParams.has("checked") &&
      searchParams.has("status") &&
      searchParams.has("amount") &&
      searchParams.get("method") === "paypal"
    ) {
      setIsScreenLoading(true);
      setInitialDisclosureAccept(true);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      searchParams.set("checked", "true");
      const tokendata = {
        token: payPalOrder?.token,
      };
      handlePayPalPayment(tokendata);
    }

    if (
      searchParams.has("method") &&
      searchParams.has("setup_intent") &&
      searchParams.has("plansDrawer") &&
      searchParams.has("planId") &&
      searchParams.has("updateMethod")
    ) {
      setIsScreenLoading(true);
      setInitialDisclosureAccept(true);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      if (searchParams.get("method") === "card") {
        searchParams.set("checked", "true");
        handleVerifyPayment({
          intent: searchParams.get("setup_intent"),
          method: "CC",
          updateMethod: searchParams.get("updateMethod"),
          updateSchedule: searchParams.get("updateSchedule"),
          isRetry: searchParams.get("isRetry"),
        });
      } else if (searchParams.get("method") === "bank") {
        handleVerifyPayment({
          intent: searchParams.get("setup_intent"),
          method: "ACH",
          updateMethod: searchParams.get("updateMethod"),
          updateSchedule: searchParams.get("updateSchedule"),
          isRetry: searchParams.get("isRetry"),
        });
      } else if (searchParams.get("method") === "cashApp") {
        handleVerifyPayment({
          intent: searchParams.get("setup_intent"),
          method: "CAPP",
          updateMethod: searchParams.get("updateMethod"),
          updateSchedule: searchParams.get("updateSchedule"),
          isRetry: searchParams.get("isRetry"),
        });
      } else if (searchParams.get("method") === "amazonPay") {
        handleVerifyPayment({
          intent: searchParams.get("setup_intent"),
          method: "AMZP",
          updateMethod: searchParams.get("updateMethod"),
          updateSchedule: searchParams.get("updateSchedule"),
          isRetry: searchParams.get("isRetry"),
        });
      }
    }
  }, []);

  const checkPastAmount = async ({
    method,
    amount,
  }: {
    method?: string;
    amount: number;
  }) => {
    try {
      let payload = {
        accounts_id: userData?.data?.accountData?.id,
        payment_method: method ? method : cardToShortname(selectedMethod),
        amount: amount,
      };
      let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
      const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
      if (isDuplicateOrders && duplicateOrdersCount > 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const handleCheckDisclosureAndDuplicate = async ({
    method,
    amount,
  }: {
    amount: number;
    method?: string;
  }) => {
    try {
      let moveAhead = !checkedDuplicatePayment
        ? await checkPastAmount({ method, amount })
        : true;
      if (!moveAhead) {
        dispatch(onSetFinalDisclosureLoading(false));
        setIsUpdateRetryLoading(false);
        setIsUpdateLoading(false);
        return false;
      } else {
        if (
          (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
            portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT) &&
          !showFinalDisclosure
        ) {
          dispatch(onSetFinalDisclosureLoading(false));
          dispatch(resetDuplicateOrder());
          dispatch(onSetShowDisclosure(true));

          return false;
        } else {
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
          return true;
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const submitScheduleOnlyUpdate = async (isRetry?: string) => {
    const existingMethod =
      paymentPlanDataRedux?.clientCustomerData?.payment_method ||
      getPaymentMethodCode(selectedMethod);
    const scheduleOnlyFlags = {
      updateMethod: false,
      updateSchedule,
    };
    const paymentPayload = {
      schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
      ...getPaymentPlanUpdateFields(existingMethod, data, scheduleOnlyFlags),
      accounts_id: userData?.data?.accountData?.accounts_id || "",
      user_id: userData?.data?.accountData?.leadData?.user_id || "",
      accountid: userData?.data?.accountData?.accountid || "",
      customer_device: getOperatingSystem(),
      clusterid: userData?.data?.accountData?.clusterid,
      x_payment_method: existingMethod,
      isRetry: Boolean(isRetry),
    };
    try {
      const response: any = await dispatchPaymentPlanUpdate(paymentPayload);
      if (!response) return;
      const responseBody = response?.body;
      const failure = getUpdatePlanFailure(responseBody);

      if (failure) {
        showPlanUpdateFailure(responseBody);
        return;
      }

      applyAuthoritativeUpdateResponse(responseBody?.data, scheduleOnlyFlags);
      setPaymentScheduleId(
        responseBody?.data?.schedule_id ||
          paymentPlanDataRedux?.clientCustomerData?.id
      );
      setIsUpdatePaymentPlan(true);
      setPlanSuccess(true);
    } catch (error) {
      showPlanUpdateFailure(undefined, error);
    } finally {
      setIsUpdateLoading(false);
      setIsUpdateRetryLoading(false);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
    }
  };

  const onUpdatePaymentPlan = async (isRetry?: string) => {
    if (
      updateFlowInFlightRef.current ||
      isAnyUpdateLoading ||
      (!hasPlanChanges && !isRetry)
    ) {
      return;
    }

    updateFlowInFlightRef.current = true;
    try {
      setIsDeclinedFailure(false);
      setFailedReason(
        "We couldn't update your payment plan. Please try again."
      );
      if (isRetry) {
        setIsUpdateRetryLoading(true);
      } else {
        setIsUpdateLoading(true);
      }
      const amount =
        data?.paymentPlan?.transactions
          ?.filter(
            (transaction: any) =>
              transaction?.status === "Pending" &&
              moment(
                transaction?.date ||
                  transaction?.payment_date ||
                  transaction?.billing_date
              ).isSame(moment(), "day")
          )
          ?.reduce(
            (sum: number, transaction: any) =>
              sum + Number(transaction?.amount || 0),
            0
          ) || Number(data?.paymentPlan?.amount || 0);

      let moveAhead = await handleCheckDisclosureAndDuplicate({
        method: getMethod(isAddNewCard, dataPayment, selectedMethod),
        amount: amount,
      });

      if (!moveAhead) {
        setIsUpdateRetryLoading(false);
        setIsUpdateLoading(false);
        return;
      }

      if (!updateMethod) {
        await submitScheduleOnlyUpdate(isRetry);
      } else if (!isAddNewCard) {
        if (selectedMethod === "Wire") {
          await paymentViaWire(isRetry);
        } else {
          await handleCreateNewCard(values, isRetry);
        }
      } else {
        let slug = decode(id || "")?.split("_");
        let method = getMethod(isAddNewCard, dataPayment, selectedMethod);
        if (
          dataPayment?.selectedBank &&
          Object.keys(dataPayment.selectedBank).length > 0
        ) {
          method = "ACH";
        }
        if (
          dataPayment?.selectedWallet &&
          Object.keys(dataPayment.selectedWallet).length > 0
        ) {
          method = "WT";
        }

        let selectedData;
        if (method === "CC") {
          selectedData = dataPayment?.selectedCard;
        } else if (method === "WT") {
          selectedData = dataPayment?.selectedWallet;
          method = selectedData?.payment_method;
        } else {
          selectedData = dataPayment?.selectedBank;
        }

        const updateBillingAddress = getUpdateBillingAddress(
          undefined,
          selectedData,
          paymentPlanDataRedux?.clientCustomerData,
          accountData
        );

        const payment_payload = {
          schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
          ...getPaymentPlanUpdateFields(method),
          accounts_id: userData?.data?.accountData?.accounts_id || "",
          accountid: userData?.data?.accountData?.accountid || "",
          customer_device: getOperatingSystem(),
          user_id: userData?.data?.accountData?.leadData?.user_id || "",
          x_payment_method: method || "",
          email: savedCardsEmail,
          x_card_num: selectedData?.cc_4digit,
          x_card_code: selectedData?.cc_cvv,
          x_exp_date: selectedData?.cc_expiry,
          iconType: selectedData?.cc_type,
          routing_number: selectedData?.routing_number,
          account_number: selectedData?.account_number,
          cc_token: selectedData?.cc_token,
          cc_token2: selectedData?.cc_token2,
          stripe_token: selectedData?.cc_token,
          bankName: selectedData?.bank_name,
          ...updateBillingAddress,
          choldername: selectedData?.account_name,
          cppid: selectedData?.id,
          isRetry: isRetry ? true : false,
          bank_name: selectedData?.bank_name || "",
          bank_address: "",
          cc_expiry: selectedData?.cc_expiry,
          cc_cvv: selectedData?.cc_cvv,
          cc_type: selectedData?.cc_type,
          cc_4digit: selectedData?.cc_4digit,
          clusterid: Number(slug[1]),
          is_3ds: false,
          use_old_card: "1",
          x_email:
            paymentPlanDataRedux?.clientCustomerData?.email ||
            userData?.data?.accountData?.email ||
            "",
        };
        if (isRetry) {
          setIsUpdateRetryLoading(true);
        } else {
          setIsUpdateLoading(true);
        }
        setPaymentScheduleId(paymentPlanDataRedux?.clientCustomerData?.id);
        try {
          const response: any =
            await dispatchPaymentPlanUpdate(payment_payload);
          if (!response) return;
          const responseBody = response?.body;
          // The server reports success for the plan even when a retried charge
          // was declined, so the payment outcome has to be checked separately.
          const failure = getUpdatePlanFailure(responseBody);
          if (!failure) {
            applyAuthoritativeUpdateResponse(responseBody?.data);
            let email = [];
            email.push(dataPayment?.email);
            let values = {
              emails: email,
              schedule_id: paymentPlanDataRedux?.clientCustomerData?.id,
            };
            // await sendEmail(values);

            setIsUpdatePaymentPlan(true);
            setPlanSuccess(true);
          } else {
            showPlanUpdateFailure(responseBody);
          }
        } catch (error) {
          showPlanUpdateFailure(undefined, error);
        } finally {
          setIsUpdateLoading(false);
          setIsUpdateRetryLoading(false);
          dispatch(onSetFinalDisclosureLoading(false));
          dispatch(onSetShowDisclosure(false));
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
        }
      }
    } finally {
      updateFlowInFlightRef.current = false;
    }
  };

  const handlePrev = () => {
    const tmp = Array.from(completedSteps);
    tmp.pop(); // Remove the last completed step when going back
    setCurrentStep(currentStep - 1);
    setCompletedSteps(tmp);
  };

  const handleSave = () => {
    // A schedule change is recalculated and validated asynchronously. Keep the
    // customer on this step until that work finishes so an invalid Custom row
    // cannot be carried into payment-method selection.
    if (
      isLoading ||
      isApiCall ||
      isEndDateError ||
      paymentPlanBeforeAddRef.current
    ) {
      return;
    }

    const missingDateError = getMissingScheduleDateError(
      data?.paymentPlan?.transactions
    );
    if (missingDateError) {
      setDateErrMsg(missingDateError);
      setIsEndDateError(true);
      return;
    }

    const tmp = Array.from(completedSteps);
    tmp.push(currentStep);
    setCompletedSteps(tmp);
    setCurrentStep((prevCurr) => prevCurr + 1);
  };

  const createPayPalIntent = async (isRetry: any) => {
    if (isRetry) {
      setIsUpdateRetryLoading(true);
    } else {
      setIsUpdateLoading(true);
    }
    try {
      const payload = {
        is_recurring: "Y",
        phone_number: userData?.data?.accountData?.phone,
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name,
        accountid: userData?.data?.accountData?.accountid,
        amount: data?.paymentPlan.amount,
        agent: userData?.data?.accountData?.lead_owner,
        lead_owner: userData?.data?.accountData?.lead_owner,
        lead_id: accountLeadData?.lead_id,
        clusterid: userData?.data?.accountData?.clusterid,
        success_url: `${
          window.location.href
        }?plansDrawer=true&planId=${id}&method=paypal&amount=${
          data?.paymentPlan.amount
        }&status=success&isRecurring=${true}`,
        cancel_url: `${
          window.location.href
        }?plansDrawer=true&planId=${id}&method=paypal&amount=${
          data?.paymentPlan.amount
        }&status=cancel&isRecurring=${true}`,
        start_date: new Date(),
      };
      let res: any = await dispatch(createPaypalToken(payload));
      if (res?.payload?.body?.success) {
        await dispatch(
          storePayPalOrder({
            url: res?.payload?.body?.result?.url,
            token: res?.payload?.body?.result?.token,
            data: data,
            dataPayment: dataPayment,
            isRetry: isRetry,
            updateFlags: currentUpdateFlags,
          })
        );
        window.location.href = res?.payload?.body?.result?.url;
      } else {
        setPlanSuccess(false);
        setIsUpdatePaymentPlan(true);
        setFailedReason("Unable to fetch paypal url");
      }
    } catch (error) {
      console.log("createPayPalIntent error =====", error);
    } finally {
      setIsUpdateLoading(false);
      setIsUpdateRetryLoading(false);
    }
  };

  const onPaymentDownload = async (id: any) => {
    setIsDownloadLoading(true);
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    try {
      let payload = {
        invoice_id: id,
        lead_id: accountLeadData?.lead_id,
        url: `${vanityUrl}view-payment/${pid}`,
        cluster_id: clusterId,
        isFromPortal: true,
      };
      await downloadPdfFromApi(
        () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
        `Receipt - ${id}.pdf`,
        (loading) => setIsDownloadLoading(loading ? true : false)
      );
    } catch (error: any) {
      console.log("payment download err: -->", error?.message);
    }
  };

  const handlePlaidBankPayment = async (
    val: any,
    isCCTokenAvailable: boolean
  ) => {
    try {
      let res: any = {};
      if (!isCCTokenAvailable) {
        let payload = await createPayload(val);
        res = await dispatch(addCard(payload)).unwrap();
      }
      let dataPayment = data.paymentPlan;
      const bankFlags = { ...currentUpdateFlags, updateMethod: true };

      const payment_payload = {
        schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
        ...getPaymentPlanUpdateFields("ACH", data, bankFlags),
        accounts_id: userData?.data?.accountData?.accounts_id || "",
        user_id: userData?.data?.accountData?.leadData?.user_id || "",
        x_payment_method: "ACH",
        cc_token:
          (isCCTokenAvailable
            ? val.cc_token
            : res?.body?.data?.paymentMethod.cc_token) || "",
        cc_token2:
          (isCCTokenAvailable
            ? val.cc_token2
            : res?.body?.data?.paymentMethod.cc_token2) || "",
        cppid:
          (isCCTokenAvailable
            ? val.cppid
            : res?.body?.data?.paymentMethod?.id) || "",
        isRetry: true || "",
        routing_number: val.routing_number,
        account_number: val.account_number,
        bank_name: val?.bankName,
        holdername: val.choldername || "",
        bank_address: "",
        cc_expiry: "",
        cc_cvv: "",
        cc_type: "",
        cc_4digit: "",
        accountid: userData?.data?.accountData?.accountid || "",
        customer_device: getOperatingSystem(),
        clusterid: userData?.data?.accountData?.clusterid,
        is_3ds: false,
        pc_atid: val.pc_atid,
      };
      const response: any = await dispatchPaymentPlanUpdate(payment_payload);
      if (!response) return;
      if (response?.body?.success) {
        applyAuthoritativeUpdateResponse(response?.body?.data, bankFlags);
        let email = [];
        email.push(dataPayment?.email);
        let values = {
          emails: email,
          schedule_id: response?.body?.data?.schedule_id,
        };
        // await sendEmail(values);
        setPaymentScheduleId(response?.body?.data?.schedule_id);
        setIsUpdatePaymentPlan(true);
        setPlanSuccess(true);
      } else {
        setIsUpdatePaymentPlan(true);
        setPlanSuccess(false);
      }
    } catch (error) {
      console.log("handlePlaidBankPayment error =====", error);
      setIsUpdateLoading(false);
      setIsUpdateRetryLoading(false);
      setIsUpdatePaymentPlan(true);
      setPlanSuccess(false);
    }
  };

  const verifyStripeCardPayment = async ({
    intent,
    email,
    error,
    orderId,
    updateMethod,
  }: {
    intent: string | null;
    email: string;
    error: string | null;
    orderId?: number;
    updateMethod: boolean;
  }) => {
    if (error) {
      setIsUpdatePaymentPlan(true);
      setPlanSuccess(false);
      setIsUpdateRetryLoading(false);
      setIsUpdateLoading(false);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      setIsScreenLoading(false);
    } else {
      try {
        const dataPayment = data?.paymentPlan || {};
        let clusterid = clusterId
          ? clusterId
          : userData?.data?.accountData?.clusterid;
        const stripeFlags = {
          ...currentUpdateFlags,
          updateMethod: updateMethod || currentUpdateFlags.updateMethod,
        };

        const payment_payload = {
          schedule_id: paymentPlanDataRedux?.clientCustomerData?.id || "",
          ...getPaymentPlanUpdateFields("CC", data, stripeFlags),
          accounts_id: userData?.data?.accountData?.accounts_id || "",
          user_id: userData?.data?.accountData?.leadData?.user_id || "",
          x_payment_method: "CC",
          cc_token: "",
          cppid: "",
          isRetry: updateMethod,
          routing_number: "",
          account_number: "",
          bank_name: "",
          choldername: paymentPlanDataRedux?.clientCustomerData?.cc_name || "",
          bank_address: "",
          cc_expiry: "",
          cc_cvv: "",
          cc_type: "",
          cc_4digit: "",
          accountid: userData?.data?.accountData?.accountid || "",
          customer_device: getOperatingSystem(),
          clusterid: clusterid,
          transaction_id: intent,
          is_3ds: true,
        };
        const response: any = await dispatchPaymentPlanUpdate(payment_payload);
        if (!response) return;
        if (response?.body?.success) {
          applyAuthoritativeUpdateResponse(response?.body?.data, stripeFlags);
          let email = [];
          email.push(dataPayment?.email);
          let values = {
            emails: email,
            schedule_id: response?.body?.data?.schedule_id,
          };
          // await sendEmail(values);
          setPaymentScheduleId(response?.body?.data?.schedule_id);
          setIsUpdatePaymentPlan(true);
          setPlanSuccess(true);
        } else {
          setIsUpdatePaymentPlan(true);
          setPlanSuccess(false);
        }
      } catch (error) {
        console.log("handleVerifyPayment error =====", error);

        setIsUpdatePaymentPlan(true);
        setPlanSuccess(false);
      } finally {
        setIsUpdateRetryLoading(false);
        setIsUpdateLoading(false);
        dispatch(onSetFinalDisclosureLoading(false));
        dispatch(onSetShowDisclosure(false));
        setIsScreenLoading(false);
      }
    }
  };

  const generalMarkers = getGeneralDetailMarkers({
    conFee: 0,
    paymentAmount: "0",
  });

  return (
    <>
      {!isFinalPreview && (
        <div
          className={`relative w-full bg-white h-[var(--shell-vh)] lg:overflow-y-clip overflow-y-scroll lg:px-10 pt-10 pb-[10px] `}
          data-testid={tid("update-plan-drawer")}
          aria-label="Update payment plan drawer"
        >
          {isScreenLoading ? (
            <div
              className="w-full h-full flex justify-center items-center"
              data-testid={tid("update-plan-loading")}
              aria-label="Loading update payment plan"
            >
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            </div>
          ) : (
            <>
              {!isUpdatePaymentPlan ? (
                <div
                  className="contents"
                  data-testid={tid("update-plan-editor")}
                  aria-label="Update payment plan editor"
                >
                  <UpdatePlanHeader
                    id={id}
                    onClose={onClose}
                    onPreview={() => {
                      safeSetUpdateData({
                        ...data,
                        __previewMode: "full",
                        __previewMethod: selectedMethod,
                      });
                      safeSetIsPreview(true);
                      safeSetHandleDrawerSize(true);
                      safeSetIsPreviewId(decode(id)?.split("_")[0]);
                    }}
                    onDispatch={dispatch}
                  />
                  <UpdatePlanContent
                    initialDisclosureAccept={initialDisclosureAccept}
                    generalMarkers={generalMarkers}
                    onDisclosureAccept={() => setInitialDisclosureAccept(true)}
                    portalSettings={portalSettings}
                    planId={id}
                    values={values}
                    isUpdateLoading={isUpdateLoading}
                    isUpdateRetryLoading={isUpdateRetryLoading}
                    setValues={setValues}
                    currentStep={currentStep}
                    selectedMethod={selectedMethod}
                    hasPaymentMethodChanged={updateMethod}
                    hasScheduleChanged={updateSchedule}
                    onPaymentMethodChange={handlePaymentMethodChange}
                    setSelectedMethod={(val) => {
                      setSelectedMethod(val);
                      setMethodPreview(val);
                    }}
                    completedSteps={completedSteps}
                    setIsStartDate={setIsStartDate}
                    data={data}
                    setData={handleScheduleDataChange}
                    isStartDate={isStartDate}
                    setIsApiCall={safeSetIsApiCall}
                    handleDebouncedChange={handleDebouncedChange}
                    handleSave={handleSave}
                    dataPayment={dataPayment}
                    setDataPayment={setDataPayment}
                    addNewRow={addNewRow}
                    removeRow={removeRow}
                    handleChange={handleChange}
                    isLoading={isLoading}
                    accountLeadData={accountLeadData}
                    setCompletedSteps={setCompletedSteps}
                    setCurrentStep={setCurrentStep}
                    isDataLoading={isDataLoading}
                    setShowPreview={(val: boolean) => {
                      safeSetUpdateData({
                        ...data,
                        __previewMode: "schedule",
                        __previewMethod: selectedMethod,
                      });
                      safeSetIsPreview(val);
                      safeSetIsPreviewId(decode(id)?.split("_")[0]);
                      safeSetHandleDrawerSize(true);
                    }}
                    isAddNewCard={isAddNewCard}
                    setIsAddNewCard={setIsAddNewCard}
                    setSavedCardsEmail={setSavedCardsEmail}
                    handlePrev={handlePrev}
                    setIsSelectStartDate={setIsSelectStartDate}
                    show={show}
                    isRetryPayment={shouldShowRetryPayment}
                    createPayPalIntent={createPayPalIntent}
                    onUpdatePaymentPlan={onUpdatePaymentPlan}
                    failedPayment={failedPayment}
                    setShowButtons={setShowButtons}
                    showButtons={
                      currentStep === 2 &&
                      !isUpdatePaymentPlan &&
                      !shouldShowRetryPayment &&
                      showButtons
                    }
                    setIsPreview={(val: boolean) => {
                      safeSetUpdateData({
                        ...data,
                        __previewMode: "full",
                        __previewMethod: selectedMethod,
                      });
                      safeSetIsPreview(val);
                      safeSetIsPreviewId(decode(id)?.split("_")[0]);
                      safeSetHandleDrawerSize(true);
                    }}
                    handleApplePay={handleApplePay}
                    isGoogleLoading={isGoogleLoading}
                    handleGooglePay={handleGooglePay}
                    isDisabled={isUpdateDisabled}
                    setIsDisabled={setIsDisabled}
                    handlePlaidBankPayment={handlePlaidBankPayment}
                    verifyStripeCardPayment={verifyStripeCardPayment}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    isEndDateError={isEndDateError}
                    dateErrMsg={dateErrMsg}
                    setDateErrMsg={setDateErrMsg}
                  />
                </div>
              ) : planSuccess ? (
                <div
                  className="contents"
                  data-testid={tid("update-plan-success-state")}
                  aria-label="Update payment plan success"
                >
                  {isWirePayment ? (
                    <SuccessWirePayment
                      wireDetails={wireDetails}
                      invoiceId={invoiceId}
                      sendEmail={sendEmail}
                      leadId={accountLeadData?.lead_id}
                      onClose={onClose}
                    />
                  ) : shouldShowRetryPayment ? (
                    <SuccessRetryPayment
                      failedPayment={failedPayment}
                      emailDisplay={emailDisplay}
                      paymentScheduleId={payment_scheduleId}
                      isEmailLoading={isEmailLoading}
                      isDownloadLoading={isDownloadLoading}
                      onClose={onClose}
                      onNavigateDashboard={() => {
                        navigate(`/${cluster_id}/dashboard`);
                        onClose();
                      }}
                      onViewDetails={() => {
                        safeSetFinalPreview(true);
                        safeSetFinalPreviewId(payment_scheduleId);
                        safeSetHandleDrawerSize(true);
                      }}
                      onSendEmail={async (email: string) => {
                        setEmailInput(email);
                        // await sendEmail({
                        //   schedule_id: payment_scheduleId,
                        //   emails: [email],
                        // });
                      }}
                      onDownload={onPaymentDownload}
                    />
                  ) : (
                    <SuccessDefaultPayment
                      paymentScheduleId={payment_scheduleId}
                      onClose={onClose}
                      onViewDetails={() => {
                        safeSetFinalPreview(true);
                        safeSetFinalPreviewId(payment_scheduleId);
                        safeSetHandleDrawerSize(true);
                      }}
                      onNavigateDashboard={() => {
                        navigate(`/${cluster_id}/dashboard`);
                        onClose();
                      }}
                    />
                  )}
                </div>
              ) : (
                <div
                  className="contents"
                  data-testid={tid("update-plan-failed-state")}
                  aria-label="Update payment plan failed"
                >
                  <FailedPayment
                    failedReason={failedReason}
                    isDeclined={isDeclinedFailure}
                    onClose={onClose}
                    onRetry={handleBackToStart}
                    onNavigateDashboard={() => {
                      navigate(`/${cluster_id}/dashboard`);
                      onClose();
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <UpdateButtons
        isUpdateLoading={isUpdateLoading}
        isDisabled={isUpdateDisabled}
        onBack={handlePrev}
        onUpdate={onUpdatePaymentPlan}
        show={show}
        currentStep={currentStep}
        isUpdatePaymentPlan={isUpdatePaymentPlan}
        isRetryPayment={shouldShowRetryPayment}
        isFinalPreview={isFinalPreview}
        showButtons={showButtons}
        initialDisclosureAccept={initialDisclosureAccept}
        showFinalDisclosure={showFinalDisclosure}
      />
      <MessageToast
        variant="warning"
        content={emailDeliveryWarning}
        isProgressBar
        width="w-full max-w-[810px] px-5"
        className="!right-1/2 !translate-x-1/2"
        onCloseClick={() => setEmailDeliveryWarning(null)}
      />
    </>
  );
};

export default UpdatePaymentPlan;
